import { useGlobalQuery, useGlobalMutation } from '@/state/query-client/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  getHotWheels,
  getHotWheel,
  insertHotWheel,
  updateHotWheel,
  deleteHotWheel,
  getWishlist,
  insertWishlist,
  updateWishlist,
  deleteWishlist,
  HotWheelsQueryParams,
  WishlistQueryParams,
} from '../services/hotwheels.service';
import {
  HotWheelInsertInput,
  HotWheelUpdateInput,
  WishlistInsertInput,
  WishlistUpdateInput,
} from '../types/hotwheels.types';

/**
 * React Query hooks for Hot Wheels + Wishlist.
 *
 * Conventions:
 * - Queries use `useGlobalQuery` (caches 5 min, no automatic retries, surfaces errors as toasts).
 * - Mutations use `useGlobalMutation` and invalidate the relevant query keys on success.
 * - All hooks follow the same patterns as `use-inventory.ts`.
 */

export const hotWheelsQueryKey = (params: HotWheelsQueryParams) => ['hotwheels', params];
export const hotWheelDetailKey = (itemId: string) => ['hotwheel', itemId];
export const wishlistQueryKey = (params: WishlistQueryParams) => ['wishlist', params];

// ---------------------------------------------------------------------------
// Hot Wheels
// ---------------------------------------------------------------------------

export const useGetHotWheels = (params: HotWheelsQueryParams) => {
  return useGlobalQuery({
    queryKey: hotWheelsQueryKey(params),
    queryFn: () => getHotWheels(params),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetHotWheel = (itemId: string | undefined) => {
  return useGlobalQuery({
    queryKey: hotWheelDetailKey(itemId ?? ''),
    queryFn: () => getHotWheel(itemId!),
    enabled: Boolean(itemId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useInsertHotWheel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<{ itemId: string | null }, Error, HotWheelInsertInput>({
    mutationKey: ['insertHotWheel'],
    mutationFn: insertHotWheel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotwheels'] });
      toast({
        title: t('CAR_ADDED'),
        description: t('CAR_ADDED_DESC'),
      });
    },
  });
};

export const useUpdateHotWheel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<
    { acknowledged: boolean; message: string | null },
    Error,
    { itemId: string; input: HotWheelUpdateInput }
  >({
    mutationKey: ['updateHotWheel'],
    mutationFn: ({ itemId, input }) => updateHotWheel(itemId, input),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['hotwheels'] });
      queryClient.invalidateQueries({ queryKey: hotWheelDetailKey(vars.itemId) });
      toast({
        title: t('CAR_UPDATED'),
        description: t('CAR_UPDATED_DESC'),
      });
    },
  });
};

export const useDeleteHotWheel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<
    { acknowledged: boolean; totalImpactedData: number },
    Error,
    { itemId: string }
  >({
    mutationKey: ['deleteHotWheel'],
    mutationFn: ({ itemId }) => deleteHotWheel(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotwheels'] });
      toast({
        title: t('CAR_DELETED'),
        description: t('CAR_DELETED_DESC'),
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Wishlist
// ---------------------------------------------------------------------------

export const useGetWishlist = (params: WishlistQueryParams) => {
  return useGlobalQuery({
    queryKey: wishlistQueryKey(params),
    queryFn: () => getWishlist(params),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useInsertWishlist = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<{ itemId: string | null }, Error, WishlistInsertInput>({
    mutationKey: ['insertWishlist'],
    mutationFn: insertWishlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      toast({ title: t('WISHLIST_ADDED') });
    },
  });
};

export const useUpdateWishlist = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<
    { acknowledged: boolean; message: string | null },
    Error,
    { itemId: string; input: WishlistUpdateInput }
  >({
    mutationKey: ['updateWishlist'],
    mutationFn: ({ itemId, input }) => updateWishlist(itemId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      toast({ title: t('WISHLIST_UPDATED') });
    },
  });
};

export const useDeleteWishlist = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation<{ acknowledged: boolean }, Error, { itemId: string }>({
    mutationKey: ['deleteWishlist'],
    mutationFn: ({ itemId }) => deleteWishlist(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      toast({ title: t('WISHLIST_DELETED') });
    },
  });
};